<template>
  <main
    v-if="$route.name === 'listini-edit-item'"
    id="create-item-page"
    class="page-container"
  >
    <button type="button" class="back-btn" @click="goBack">
      <i class="fa-solid fa-arrow-left"></i> Indietro
    </button>
    <form class="create-item-form" @submit.prevent="onUpdateItem">
      <!-- ── Campi comuni ─────────────────────────────────────────── -->
      <div class="create-item-row">
        <div class="field">
          <label for="item-code">Codice articolo *</label>
          <input
            id="item-code"
            type="text"
            v-model.trim="formData.itemCode"
            placeholder="es. ART-001"
            maxlength="255"
          />
        </div>
        <div class="field">
          <label for="item-name">Nome articolo *</label>
          <input
            id="item-name"
            type="text"
            v-model="formData.itemName"
            placeholder="inserisci"
          />
          <small class="help-note">Oppure il brand: basta uno dei due</small>
        </div>
      </div>

      <div class="create-item-row">
        <div class="field">
          <label for="brand">Brand</label>
          <input
            id="brand"
            type="text"
            v-model="formData.brand"
            placeholder="inserisci"
          />
        </div>
        <div class="field">
          <label for="serie">Serie</label>
          <input
            id="serie"
            type="text"
            v-model="formData.serie"
            placeholder="inserisci"
          />
        </div>
        <div class="field">
          <label for="articleStatus">Stato</label>
          <StatusSelect
            v-model="formData.articleStatus"
            :options="articleStatusOptions"
          />
        </div>
      </div>

      <!-- Prezzi: IVA esclusa -->
      <div class="create-item-row">
        <div class="field">
          <label for="price">Prezzo listino *</label>
          <input
            id="price"
            type="number"
            step="any"
            v-model="formData.price"
            placeholder="inserisci"
            :class="{ invalid: vendita.errori.price }"
          />
          <small v-if="vendita.errori.price" class="help-error">{{
            vendita.errori.price
          }}</small>
          <small v-else class="help-note">IVA esclusa</small>
        </div>
        <div class="field">
          <label for="priceDiscounted">Prezzo scontato</label>
          <input
            id="priceDiscounted"
            type="number"
            step="0.01"
            min="0"
            v-model="formData.priceDiscounted"
            placeholder="facoltativo"
            :class="{ invalid: erroreScontato }"
          />
          <small v-if="erroreScontato" class="help-error">{{
            erroreScontato
          }}</small>
          <small v-else class="help-note">IVA esclusa</small>
        </div>
        <!-- [sc-49299] l'unità di vendita decide quali misure servono -->
        <div class="field">
          <label for="salesUnit">Venduto a *</label>
          <StatusSelect
            v-model="formData.salesUnit"
            :options="unitaVendita"
            placeholder="— scegli —"
          />
          <small
            v-if="avvisoUnita(category, formData.salesUnit)"
            class="help-warning"
            >{{ avvisoUnita(category, formData.salesUnit) }}</small
          >
        </div>
      </div>

      <hr />

      <!-- ── Gruppo AREA (piastrelle, rivestimenti, parquet, laminati, decking) ── -->
      <template v-if="categoryGroup === 'area'">
        <div class="create-item-row">
          <div class="field">
            <label for="formatLabel">Formato (es. 60x120)</label>
            <input
              id="formatLabel"
              type="text"
              v-model="formData.formatLabel"
              placeholder="es. 60x120"
            />
          </div>
          <div class="field">
            <label for="thicknessMm">Spessore (mm)</label>
            <input
              id="thicknessMm"
              type="number"
              step="any"
              v-model="formData.thicknessMm"
              placeholder="es. 10"
            />
          </div>
          <div class="field">
            <label for="surface">Superficie</label>
            <input
              id="surface"
              type="text"
              v-model="formData.surface"
              placeholder="es. Matt, Lucido"
            />
          </div>
        </div>
        <div class="create-item-row">
          <div class="field">
            <label for="color">Colore</label>
            <input
              id="color"
              type="text"
              v-model="formData.color"
              placeholder="inserisci"
            />
          </div>
          <div class="field">
            <label for="piecesPerBox"
              >Pz / scatola{{
                richiesto(formData.salesUnit, "piecesPerBox") ? " *" : ""
              }}</label
            >
            <input
              id="piecesPerBox"
              type="number"
              min="0"
              v-model.number="formData.piecesPerBox"
              placeholder="inserisci"
              :class="{ invalid: vendita.errori.piecesPerBox }"
            />
            <small v-if="vendita.errori.piecesPerBox" class="help-error">{{
              vendita.errori.piecesPerBox
            }}</small>
          </div>
          <div class="field">
            <label for="sqmPerBox"
              >M² / scatola{{
                richiesto(formData.salesUnit, "sqmPerBox") ? " *" : ""
              }}</label
            >
            <input
              id="sqmPerBox"
              type="number"
              step="any"
              min="0"
              v-model.number="formData.sqmPerBox"
              placeholder="inserisci"
              :class="{ invalid: vendita.errori.sqmPerBox }"
            />
            <small v-if="vendita.errori.sqmPerBox" class="help-error">{{
              vendita.errori.sqmPerBox
            }}</small>
          </div>
        </div>
      </template>

      <!-- ── Gruppo PIECE (sanitari, rubinetteria, box doccia, accessori) ── -->
      <template v-if="categoryGroup === 'piece'">
        <div class="create-item-row">
          <div v-if="category === 'sanitari'" class="field">
            <label for="fixtureType">Tipo</label>
            <StatusSelect
              v-model="formData.fixtureType"
              :options="fixtureTypeOptions"
            />
          </div>
          <div v-if="category === 'sanitari'" class="field">
            <label for="fixturePosition">Posizione</label>
            <StatusSelect
              v-model="formData.fixturePosition"
              :options="fixturePositionOptions"
            />
          </div>
          <div class="field">
            <label for="finish">Finitura</label>
            <input
              id="finish"
              type="text"
              v-model="formData.finish"
              placeholder="es. Cromo, Nero opaco"
            />
          </div>
          <div class="field">
            <label for="color">Colore</label>
            <input
              id="color"
              type="text"
              v-model="formData.color"
              placeholder="inserisci"
            />
          </div>
        </div>
      </template>

      <!-- ── Gruppo LINEAR (profili, sistemi scarico) ── -->
      <template v-if="categoryGroup === 'linear'">
        <div class="create-item-row">
          <div class="field">
            <label for="material">Materiale</label>
            <input
              id="material"
              type="text"
              v-model="formData.material"
              placeholder="es. Alluminio, Inox"
            />
          </div>
          <div class="field">
            <label for="lengthMm">Lunghezza (mm)</label>
            <input
              id="lengthMm"
              type="number"
              step="any"
              min="0"
              v-model.number="formData.lengthMm"
              placeholder="es. 2500"
            />
          </div>
          <div class="field">
            <label for="mlPerBox"
              >ML / confezione{{
                richiesto(formData.salesUnit, "mlPerBox") ? " *" : ""
              }}</label
            >
            <input
              id="mlPerBox"
              type="number"
              step="any"
              min="0"
              v-model.number="formData.mlPerBox"
              placeholder="es. 2.5"
              :class="{ invalid: vendita.errori.mlPerBox }"
            />
            <small v-if="vendita.errori.mlPerBox" class="help-error">{{
              vendita.errori.mlPerBox
            }}</small>
          </div>
        </div>
      </template>

      <!-- ── Gruppo FURNITURE (mobili, porte e infissi) ── -->
      <template v-if="categoryGroup === 'furniture'">
        <div class="create-item-row">
          <div class="field">
            <label for="widthCm">Larghezza (cm)</label>
            <input
              id="widthCm"
              type="number"
              step="any"
              min="0"
              v-model.number="formData.widthCm"
              placeholder="inserisci"
            />
          </div>
          <div class="field">
            <label for="heightCm">Altezza (cm)</label>
            <input
              id="heightCm"
              type="number"
              step="any"
              min="0"
              v-model.number="formData.heightCm"
              placeholder="inserisci"
            />
          </div>
          <div class="field">
            <label for="material">Materiale</label>
            <input
              id="material"
              type="text"
              v-model="formData.material"
              placeholder="es. MDF, Rovere"
            />
          </div>
          <div class="field">
            <label for="finish">Finitura</label>
            <input
              id="finish"
              type="text"
              v-model="formData.finish"
              placeholder="es. Bianco opaco"
            />
          </div>
        </div>
      </template>

      <!-- ── Gruppo GENERIC (altro) ── -->
      <template v-if="categoryGroup === 'generic'">
        <div class="create-item-row">
          <div class="field">
            <label for="notes">Note</label>
            <textarea
              id="notes"
              v-model="formData.notes"
              placeholder="inserisci"
              rows="3"
            />
          </div>
        </div>
      </template>

      <hr />

      <div class="save-button-container">
        <button
          id="create-item-button"
          class="primary-btn"
          type="submit"
          :disabled="!isDirty || !valido"
          :aria-disabled="!isDirty || !valido"
          :title="
            !isDirty
              ? 'Modifica almeno uno dei campi'
              : erroreScontato ||
                (!valido
                  ? 'Compila i campi obbligatori (*)'
                  : 'Aggiorna articolo')
          "
        >
          Aggiorna
        </button>
      </div>
    </form>

    <VueFinalModal
      v-model="showBackModal"
      :clickToClose="true"
      :escToClose="true"
      classes="documents-modal"
      content-class="confirm-modal-content"
    >
      <template #default>
        <button
          class="modal__close icons-button"
          @click="showBackModal = false"
          title="Chiudi"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
        <h3 class="modal__title">Tornare indietro?</h3>
        <p class="modal__subtitle">Le modifiche non salvate andranno perse.</p>
        <div class="modal-actions">
          <button class="secondary-btn" @click="showBackModal = false">
            Annulla
          </button>
          <button class="primary-btn confirm-btn" @click="confirmGoBack">
            Conferma
          </button>
        </div>
      </template>
    </VueFinalModal>
  </main>
</template>

<script>
import { mapActions } from "vuex/dist/vuex.cjs.js";
import StatusSelect from "@/components/StatusSelect.vue";
import { erroreScontato, numeroPrezzo } from "@/utilities/prezzoArticolo";
import {
  avvisoUnita,
  controlloVendita,
  haNomeOBrand,
  richiesto,
  unitaPerCategoria,
} from "@/utilities/venditaArticolo";

const COLUMN_GROUPS = {
  area: [
    "piastrelle",
    "rivestimenti parete",
    "parquet",
    "laminati",
    "decking esterno",
  ],
  piece: ["sanitari", "rubinetteria", "box doccia", "accessori bagno"],
  linear: ["profili", "sistemi di scarico"],
  furniture: ["mobili", "porte e infissi"],
};

function getCategoryGroup(category) {
  for (const [group, cats] of Object.entries(COLUMN_GROUPS)) {
    if (cats.includes(category)) return group;
  }
  return "generic";
}

export default {
  name: "EditItemPage",
  components: { StatusSelect },
  beforeRouteLeave(to) {
    if (this.navigationConfirmed || to.name === "login") {
      this.navigationConfirmed = false;
      return true;
    }
    this.pendingNavigation = to;
    this.showBackModal = true;
    return false;
  },
  props: ["itemId"],
  data() {
    return {
      showBackModal: false,
      pendingNavigation: null,
      navigationConfirmed: false,
      articleStatusOptions: [
        { value: "attivo", label: "Attivo", color: "#059669" },
        { value: "novita", label: "Novità", color: "#2563eb" },
        { value: "discontinuo", label: "Discontinuo", color: "#9ca3af" },
      ],
      fixtureTypeOptions: [
        { value: "", label: "— scegli —" },
        { value: "vaso", label: "Vaso", icon: "fa-toilet" },
        { value: "bidet", label: "Bidet", icon: "fa-water" },
        // [sc-48893] tipi nuovi, assegnati anche ai sanitari del FILE UNICO
        { value: "coprivaso", label: "Coprivaso", icon: "fa-toilet-paper" },
        { value: "cassetta", label: "Cassetta", icon: "fa-box-archive" },
        { value: "lavabo", label: "Lavabo", icon: "fa-droplet" },
        { value: "box doccia", label: "Box doccia", icon: "fa-shower" },
        { value: "piatto doccia", label: "Piatto doccia", icon: "fa-grip" },
        { value: "vasca", label: "Vasca", icon: "fa-bath" },
        {
          value: "mobile bagno",
          label: "Mobile bagno",
          icon: "fa-layer-group",
        },
        { value: "specchiera", label: "Specchiera", icon: "fa-image" },
        { value: "rubinetteria", label: "Rubinetteria", icon: "fa-faucet" },
        { value: "accessorio", label: "Accessorio", icon: "fa-puzzle-piece" },
        { value: "decoro", label: "Decoro", icon: "fa-gem" },
        { value: "idraulica", label: "Idraulica", icon: "fa-wrench" },
        { value: "altro", label: "Altro", icon: "fa-circle-question" },
      ],
      fixturePositionOptions: [
        { value: "", label: "— scegli —" },
        { value: "sospeso", label: "Sospeso", icon: "fa-arrow-up" },
        { value: "terra", label: "A terra", icon: "fa-arrow-down" },
        { value: "mono-blocco", label: "Mono-blocco", icon: "fa-cube" },
        { value: "nessuno", label: "Nessuno", icon: "fa-ban" },
        { value: "altro", label: "Altro", icon: "fa-circle-question" },
      ],
      formData: {
        itemCode: "",
        itemName: "",
        brand: "",
        serie: "",
        price: 0,
        priceDiscounted: null,
        articleStatus: "attivo",
        salesUnit: "",
        // area
        formatLabel: "",
        thicknessMm: "",
        surface: "",
        color: "",
        piecesPerBox: "",
        sqmPerBox: "",
        // piece
        fixtureType: "",
        fixturePosition: "",
        finish: "",
        // linear
        material: "",
        lengthMm: "",
        mlPerBox: "",
        // furniture
        widthCm: "",
        heightCm: "",
        // generic
        notes: "",
        // common
        entryDate: "",
      },
      originalSnapshot: null,
      hasLoaded: false,
      errorMessage: "",
      successMessage: "",
    };
  },
  computed: {
    category() {
      return decodeURIComponent(this.$route.params.category || "");
    },
    categoryGroup() {
      return getCategoryGroup(this.category);
    },
    erroreScontato() {
      return erroreScontato(this.formData.price, this.formData.priceDiscounted);
    },
    unitaVendita() {
      return unitaPerCategoria(this.category, this.formData.salesUnit);
    },
    vendita() {
      return controlloVendita(this.formData);
    },
    // [sc-49299] obbligatori: codice, nome o brand, prezzo, unità di vendita e
    // le misure che l'unità chiede
    valido() {
      return (
        String(this.formData.itemCode || "").trim().length > 0 &&
        haNomeOBrand(this.formData) &&
        this.vendita.completo &&
        !this.erroreScontato
      );
    },
    isDirty() {
      if (!this.hasLoaded) return false;
      return (
        JSON.stringify(this.snapshotCurrent()) !==
        JSON.stringify(this.originalSnapshot)
      );
    },
  },
  methods: {
    avvisoUnita,
    richiesto,
    goBack() {
      this.pendingNavigation = null;
      this.showBackModal = true;
    },
    confirmGoBack() {
      this.navigationConfirmed = true;
      this.showBackModal = false;
      if (this.pendingNavigation) {
        this.$router.push(this.pendingNavigation);
      } else {
        // all'elenco, non alla pagina prima nella cronologia: dopo un
        // ricaricamento era di nuovo questa
        this.$router.push({
          name: "listini-category",
          params: { category: this.category },
        });
      }
    },
    ...mapActions(["updateItem"]),

    snapshotCurrent() {
      const str = (v) => (v == null ? "" : String(v).trim());
      const num = (v) => (v === "" || v == null ? null : Number(v));
      const f = this.formData;
      return {
        itemCode: str(f.itemCode),
        itemName: str(f.itemName),
        brand: str(f.brand),
        serie: str(f.serie),
        price: num(f.price),
        priceDiscounted: num(f.priceDiscounted),
        articleStatus: str(f.articleStatus),
        salesUnit: str(f.salesUnit),
        formatLabel: str(f.formatLabel),
        thicknessMm: num(f.thicknessMm),
        surface: str(f.surface),
        color: str(f.color),
        piecesPerBox: num(f.piecesPerBox),
        sqmPerBox: num(f.sqmPerBox),
        fixtureType: str(f.fixtureType),
        fixturePosition: str(f.fixturePosition),
        finish: str(f.finish),
        material: str(f.material),
        lengthMm: num(f.lengthMm),
        mlPerBox: num(f.mlPerBox),
        widthCm: num(f.widthCm),
        heightCm: num(f.heightCm),
        notes: str(f.notes),
        entryDate: str(f.entryDate),
      };
    },

    async fetchItemData() {
      try {
        const response = await this.$axios.get(`item/${this.itemId}`);
        if (response.status === 200) {
          const itemData = response.data.item;
          this.formData = {
            ...this.formData,
            ...itemData,
            entryDate: itemData.entryDate
              ? itemData.entryDate.slice(0, 10)
              : "",
          };
          this.$nextTick(() => {
            this.originalSnapshot = this.snapshotCurrent();
            this.hasLoaded = true;
            this.$toast.success(`Articolo caricato.`);
          });
        }
      } catch (error) {
        this.$toast.error(
          error.response?.data?.message || "Impossibile caricare i dati."
        );
      }
    },

    async onUpdateItem() {
      if (!this.valido) return;
      try {
        const payload = {
          itemCode: this.formData.itemCode,
          itemName: this.formData.itemName,
          brand: this.formData.brand,
          serie: this.formData.serie,
          price: this.formData.price,
          // null, non 0: uno scontato a 0 vorrebbe dire "gratis"
          priceDiscounted: numeroPrezzo(this.formData.priceDiscounted),
          articleStatus: this.formData.articleStatus,
          salesUnit: this.formData.salesUnit || null,
          category: this.category,
          formatLabel: this.formData.formatLabel || null,
          thicknessMm: this.formData.thicknessMm || null,
          surface: this.formData.surface || null,
          color: this.formData.color || null,
          piecesPerBox: this.formData.piecesPerBox || null,
          sqmPerBox: this.formData.sqmPerBox || null,
          fixtureType: this.formData.fixtureType || null,
          fixturePosition: this.formData.fixturePosition || null,
          finish: this.formData.finish || null,
          material: this.formData.material || null,
          lengthMm: this.formData.lengthMm || null,
          mlPerBox: this.formData.mlPerBox || null,
          widthCm: this.formData.widthCm || null,
          heightCm: this.formData.heightCm || null,
          notes: this.formData.notes || null,
          entryDate: this.formData.entryDate || null,
        };

        const response = await this.$store.dispatch("updateItem", {
          itemId: this.itemId,
          payload,
        });
        if (response.status === 200) {
          this.$toast.success(response.data?.message || "Articolo aggiornato!");
          this.originalSnapshot = this.snapshotCurrent();
          setTimeout(() => {
            this.$router.push({
              name: "listini-category",
              params: { category: this.category },
            });
          }, 1000);
        }
      } catch (error) {
        if (
          error.response?.status === 409 &&
          error.response?.data?.code === "ITEM_CODE_DUPLICATE"
        ) {
          this.$toast.error(
            "Questo codice è già utilizzato da un altro articolo."
          );
        } else {
          this.$toast.error(
            error.response?.data?.details ||
              error.response?.data?.message ||
              error.response?.data?.error ||
              "Errore durante l'aggiornamento."
          );
        }
      }
    },
  },
  mounted() {
    this.fetchItemData();
  },
};
</script>

<style src="@/css/CreateItemPage.css"></style>
